import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Phone, MapPin, Calendar, ExternalLink, X } from 'lucide-react';

// Translation strings local to this page (to avoid bloating LanguageContext)
const t_es = {
  pageTitle: 'Atracciones en Guadalajara',
  pageSubtitle: 'Explora la belleza, cultura y tradicion de Jalisco durante tu estancia',
  appointmentRequired: 'Requiere cita previa',
  contactTo: 'Contactar a',
  visitWebsite: 'Visitar sitio oficial',
  highlights: 'Lo destacado',
  museum: {
    name: 'Museo Judio de la Comunidad',
    desc: 'Un recorrido por la historia y herencia de la comunidad judia en Guadalajara. La visita es guiada y se realiza unicamente con cita previa.',
    highlights: ['Exhibiciones permanentes', 'Visita guiada en espanol o ingles', 'Espacio cultural y educativo']
  },
  tequila: {
    name: 'Tequila',
    desc: 'Pueblo Magico declarado Patrimonio de la Humanidad por la UNESCO. Aqui nace el tequila, entre campos infinitos de agave azul. Recorridos en las haciendas, degustaciones y tren turistico.',
    highlights: ['Paisaje Agavero (UNESCO)', 'Destilerias historicas: Jose Cuervo, Sauza, Mundo Cuervo', 'Tren Jose Cuervo Express', 'Aprox. 1 hr 15 min desde Guadalajara']
  },
  tlaquepaque: {
    name: 'Tlaquepaque',
    desc: 'Pueblo Magico famoso por sus artesanias, ceramica, vidrio soplado y arte popular mexicano. Sus calles peatonales, restaurantes y galerias lo hacen imperdible.',
    highlights: ['Calle Independencia (peatonal)', 'El Parian (musica de mariachi en vivo)', 'Galerias de arte y artesania', 'Centro de Tlaquepaque iluminado de noche']
  },
  chapala: {
    name: 'Lago de Chapala',
    desc: 'El lago natural mas grande de Mexico. Ideal para un dia tranquilo con paseos en lancha, malecon, pescado blanco y los pintorescos pueblos de Ajijic y Chapala.',
    highlights: ['Malecon de Chapala', 'Paseo a Isla de los Alacranes / Isla Mezcala', 'Pueblo de Ajijic (Pueblo Magico)', 'Aprox. 50 min desde Guadalajara']
  },
  hospicio: {
    name: 'Hospicio Cabanas',
    desc: 'Patrimonio de la Humanidad por la UNESCO. Antiguo orfanato del siglo XVIII que alberga los famosos murales de Jose Clemente Orozco, incluyendo "El Hombre de Fuego".',
    highlights: ['Murales de Orozco (1937-1939)', 'Patrimonio Mundial UNESCO', 'En el corazon del centro historico', 'Sala dedicada al "Hombre de Fuego"']
  },
  fanfest: {
    name: 'FIFA Fan Festival Guadalajara 2026',
    desc: 'El epicentro de la Copa Mundial fuera del estadio. Pantalla gigante para ver los partidos, conciertos, gastronomia y experiencias FIFA. Entrada gratuita.',
    highlights: ['Pantallas gigantes para todos los partidos', 'Conciertos y entretenimiento', 'Zona gastronomica y patrocinadores', 'Sede oficial en Guadalajara'],
    dates: '11 de junio - 19 de julio, 2026'
  },
  tonala: {
    name: 'Tonala',
    desc: 'Cuna de la artesania mexicana. Famoso por su tianguis (mercado) los jueves y domingos, donde encontraras ceramica, vidrio soplado, barro bruno y mas. Una experiencia autentica.',
    highlights: ['Tianguis de Tonala (jueves y domingos)', 'Vidrio soplado artesanal', 'Ceramica de alta temperatura', 'Talleres abiertos al publico']
  }
};

const t_en = {
  pageTitle: 'Attractions in Guadalajara',
  pageSubtitle: 'Explore the beauty, culture and tradition of Jalisco during your stay',
  appointmentRequired: 'Appointment required',
  contactTo: 'Contact',
  visitWebsite: 'Visit official site',
  highlights: 'Highlights',
  museum: {
    name: 'Jewish Community Museum',
    desc: 'A tour through the history and heritage of the Jewish community in Guadalajara. Guided tours by appointment only.',
    highlights: ['Permanent exhibitions', 'Guided tour in Spanish or English', 'Cultural and educational space']
  },
  tequila: {
    name: 'Tequila',
    desc: 'A Magical Town and UNESCO World Heritage site. Birthplace of tequila, surrounded by endless blue agave fields. Hacienda tours, tastings, and a tourist train.',
    highlights: ['Agave Landscape (UNESCO)', 'Historic distilleries: Jose Cuervo, Sauza, Mundo Cuervo', 'Jose Cuervo Express train', 'About 1h 15min from Guadalajara']
  },
  tlaquepaque: {
    name: 'Tlaquepaque',
    desc: 'Magical Town famous for its crafts, ceramics, blown glass and Mexican folk art. Its pedestrian streets, restaurants and galleries make it a must.',
    highlights: ['Independencia pedestrian street', 'El Parian (live mariachi music)', 'Art and craft galleries', 'Town center beautifully lit at night']
  },
  chapala: {
    name: 'Lake Chapala',
    desc: "Mexico's largest natural lake. Perfect for a relaxed day with boat rides, the lakeside boardwalk, white fish dishes, and the charming towns of Ajijic and Chapala.",
    highlights: ['Chapala lakeside boardwalk', 'Boat tour to Alacranes or Mezcala Island', 'Ajijic (Magical Town)', 'About 50 min from Guadalajara']
  },
  hospicio: {
    name: 'Hospicio Cabanas',
    desc: 'UNESCO World Heritage site. A former 18th-century orphanage housing the famous murals by Jose Clemente Orozco, including "The Man of Fire".',
    highlights: ['Orozco murals (1937-1939)', 'UNESCO World Heritage', 'Right in the historic center', 'Dedicated "Man of Fire" room']
  },
  fanfest: {
    name: 'FIFA Fan Festival Guadalajara 2026',
    desc: 'The World Cup epicenter outside the stadium. Giant screen, concerts, food and FIFA experiences. Free entry.',
    highlights: ['Giant screens for every match', 'Concerts and entertainment', 'Food zone and sponsors', 'Official venue in Guadalajara'],
    dates: 'June 11 - July 19, 2026'
  },
  tonala: {
    name: 'Tonala',
    desc: 'The cradle of Mexican craftsmanship. Famous for its open-air market (tianguis) on Thursdays and Sundays, with ceramics, blown glass, brown clay and more. An authentic experience.',
    highlights: ['Tonala tianguis (Thu & Sun)', 'Handcrafted blown glass', 'High-temperature ceramics', 'Workshops open to the public']
  }
};

const t_he = {
  pageTitle: 'אטרקציות בגואדלחרה',
  pageSubtitle: 'גלו את היופי, התרבות והמסורת של חליסקו במהלך השהות שלכם',
  appointmentRequired: 'נדרשת הזמנה מראש',
  contactTo: 'צור קשר עם',
  visitWebsite: 'בקרו באתר הרשמי',
  highlights: 'נקודות עניין',
  museum: {
    name: 'המוזיאון של הקהילה היהודית',
    desc: 'סיור בהיסטוריה ובמורשת של הקהילה היהודית בגואדלחרה. הסיור מודרך ומתבצע בתיאום מראש בלבד.',
    highlights: ['תערוכות קבועות', 'סיור מודרך בספרדית או באנגלית', 'מרחב תרבותי וחינוכי']
  },
  tequila: {
    name: 'טקילה',
    desc: 'עיירה קסומה ואתר מורשת עולמית של אונסק"ו. כאן נולדה הטקילה, בלב שדות אגבה כחולה אינסופיים. סיורים בהאסיינדות, טעימות ורכבת תיירותית.',
    highlights: ['נוף האגבה (אונסק"ו)', 'מזקקות היסטוריות: חוסה קוארבו, סאוסה', 'רכבת חוסה קוארבו אקספרס', 'כשעה ורבע מגואדלחרה']
  },
  tlaquepaque: {
    name: 'טלאקפאקה',
    desc: 'עיירה קסומה המפורסמת באומנות, קרמיקה, זכוכית מנופחת ואומנות עממית מקסיקנית. הרחובות להולכי הרגל, המסעדות והגלריות הופכים אותה להכרחית.',
    highlights: ['רחוב אינדפנדנסיה (להולכי רגל)', 'אל פריאן (מריאצ\'י בהופעה חיה)', 'גלריות אומנות ומלאכת יד', 'מרכז העיר מואר בלילה']
  },
  chapala: {
    name: 'אגם צ\'פלה',
    desc: 'האגם הטבעי הגדול ביותר במקסיקו. אידיאלי ליום רגוע עם שיט בסירות, טיילת, דגים לבנים והכפרים הציוריים אחיחיק וצ\'פלה.',
    highlights: ['טיילת צ\'פלה', 'שיט לאי אלקראנס / מסקלה', 'אחיחיק (עיירה קסומה)', 'כ-50 דקות מגואדלחרה']
  },
  hospicio: {
    name: 'הוספיסיו קבניאס',
    desc: 'אתר מורשת עולמית של אונסק"ו. בית יתומים לשעבר מהמאה ה-18 המארח את ציורי הקיר המפורסמים של חוסה קלמנטה אורוסקו, כולל "איש האש".',
    highlights: ['ציורי הקיר של אורוסקו (1937-1939)', 'מורשת עולמית של אונסק"ו', 'בלב המרכז ההיסטורי', 'אולם ייעודי לציור "איש האש"']
  },
  fanfest: {
    name: 'פסטיבל האוהדים FIFA גואדלחרה 2026',
    desc: 'מרכז המונדיאל מחוץ לאיצטדיון. מסך ענק, מופעים, אוכל וחוויות FIFA. כניסה חופשית.',
    highlights: ['מסכי ענק לכל המשחקים', 'מופעים ובידור', 'אזור אוכל וספונסרים', 'אתר רשמי בגואדלחרה'],
    dates: '11 ביוני - 19 ביולי, 2026'
  },
  tonala: {
    name: 'טונאלה',
    desc: 'ערש האומנות המקסיקנית. מפורסם בשוק הפתוח (טיאנגיס) בימי חמישי וראשון, עם קרמיקה, זכוכית מנופחת, חרס כהה ועוד. חוויה אותנטית.',
    highlights: ['שוק טונאלה (ה\' ו-א\')', 'זכוכית מנופחת בעבודת יד', 'קרמיקה בטמפרטורה גבוהה', 'סדנאות פתוחות לקהל']
  }
};

const translations = { es: t_es, en: t_en, he: t_he };

// Image sets per attraction
const images = {
  museum: ['/attractions/museo-1.jpg', '/attractions/museo-2.jpg', '/attractions/museo-3.jpg', '/attractions/museo-4.jpg', '/attractions/museo-5.jpg'],
  tequila: [
    'https://images.unsplash.com/photo-1694923963403-2443a0215818?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1678814949794-50630d650341?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1662610978411-ceaf6a2e8bd3?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1683313890724-ec2df503a9d6?w=1200&q=75&auto=format&fit=crop'
  ],
  tlaquepaque: [
    'https://images.unsplash.com/photo-1552929635-1d39dd7dcb33?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1617762537199-ddffffbe1ae7?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1731692761512-2d26799e0dde?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1683245871223-5a148818bb0e?w=1200&q=75&auto=format&fit=crop'
  ],
  chapala: [
    'https://images.unsplash.com/photo-1627422976727-65b493c39761?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1715181966465-f9ea73f45f9d?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1715181974899-31d128fa282c?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1727292188273-78dc77462560?w=1200&q=75&auto=format&fit=crop'
  ],
  hospicio: [
    'https://images.unsplash.com/photo-1708733656720-1002b6f61cb0?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1565670105658-ea35d27f7de7?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1709066946896-83229cd579a4?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1703220314533-4f7792046c1c?w=1200&q=75&auto=format&fit=crop'
  ],
  fanfest: [
    'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1200&q=75&auto=format&fit=crop'
  ],
  tonala: [
    'https://images.unsplash.com/photo-1703220347575-2c5218ad384d?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1731692688707-821c44dd0db6?w=1200&q=75&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1731692688304-d09f28b1d817?w=1200&q=75&auto=format&fit=crop'
  ]
};

const Lightbox = ({ src, onClose }) => (
  <div
    className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4 cursor-zoom-out"
    onClick={onClose}
    data-testid="attractions-lightbox"
  >
    <button
      onClick={onClose}
      className="absolute top-4 right-4 text-white/80 hover:text-white"
      data-testid="lightbox-close"
      aria-label="Cerrar"
    >
      <X className="w-8 h-8" />
    </button>
    <img src={src} alt="" className="max-h-[90vh] max-w-[95vw] object-contain rounded-2xl shadow-2xl" />
  </div>
);

const Section = ({ id, name, desc, highlightsTitle, highlights, photos, extra, testid }) => {
  const [lightbox, setLightbox] = useState(null);
  return (
    <section
      id={id}
      className="bg-white rounded-3xl shadow-sm border border-[#E5E3D8] overflow-hidden mb-12"
      data-testid={`attraction-section-${testid}`}
    >
      <div className="grid md:grid-cols-2 gap-0">
        {/* Hero image */}
        <div className="relative h-72 md:h-full min-h-[320px] bg-[#0F2922]">
          <img
            src={photos[0]}
            alt={name}
            className="w-full h-full object-cover cursor-zoom-in"
            onClick={() => setLightbox(photos[0])}
            loading="lazy"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 md:hidden">
            <h2 className="text-white font-heading text-2xl font-bold">{name}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 lg:p-10 flex flex-col">
          <h2 className="hidden md:block font-heading text-3xl lg:text-4xl font-bold text-[#0F2922] mb-4" data-testid={`attraction-name-${testid}`}>
            {name}
          </h2>
          <p className="font-body text-[#5C6C62] text-base leading-relaxed mb-6">{desc}</p>

          <div className="mb-6">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#B85042] mb-3">
              {highlightsTitle}
            </h3>
            <ul className="space-y-2">
              {highlights.map((h, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-[#0F2922]">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#B85042] flex-shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>

          {extra}
        </div>
      </div>

      {/* Photo gallery */}
      {photos.length > 1 && (
        <div className="px-6 md:px-8 lg:px-10 pb-6 md:pb-8 lg:pb-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.slice(1).map((src, i) => (
              <button
                key={i}
                onClick={() => setLightbox(src)}
                className="aspect-square overflow-hidden rounded-xl bg-[#FAF9F6] group"
                data-testid={`attraction-gallery-${testid}-${i}`}
              >
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </section>
  );
};

const AttractionsPage = () => {
  const { language, isRTL } = useLanguage();
  const t = translations[language] || translations.es;

  return (
    <div className={`min-h-screen bg-[#FAF9F6] pt-20 pb-16 ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section className="relative h-[280px] md:h-[340px] overflow-hidden mb-10">
        <div className="absolute inset-0 bg-[#0F2922]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F2922] via-[#0F2922]/95 to-[#B85042]/30" />
        </div>
        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-3" data-testid="attractions-title">
              {t.pageTitle}
            </h1>
            <p className="font-body text-[#FAF9F6]/80 text-base sm:text-lg max-w-2xl mx-auto">
              {t.pageSubtitle}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 1. Museo Judio */}
        <Section
          id="museo-judio"
          testid="museum"
          name={t.museum.name}
          desc={t.museum.desc}
          highlightsTitle={t.highlights}
          highlights={t.museum.highlights}
          photos={images.museum}
          extra={
            <Card className="bg-[#FAF9F6] border-[#E5E3D8] rounded-2xl">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-[#B85042] font-body font-semibold text-sm">
                  <Calendar className="w-4 h-4" />
                  {t.appointmentRequired}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="text-[#5C6C62] font-body">{t.contactTo}:</div>
                  <a
                    href="https://wa.me/523331497166"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#0F2922] hover:text-[#B85042] transition-colors"
                    data-testid="museum-contact"
                  >
                    <Phone className="w-4 h-4" />
                    <span>+52 33 3149 7166</span>
                  </a>
                </div>
              </CardContent>
            </Card>
          }
        />

        {/* 2. Tequila */}
        <Section
          id="tequila"
          testid="tequila"
          name={t.tequila.name}
          desc={t.tequila.desc}
          highlightsTitle={t.highlights}
          highlights={t.tequila.highlights}
          photos={images.tequila}
          extra={
            <div className="flex items-center gap-2 text-sm text-[#5C6C62]">
              <MapPin className="w-4 h-4 text-[#B85042]" />
              <span>Tequila, Jalisco - 65 km de Guadalajara</span>
            </div>
          }
        />

        {/* 3. Tlaquepaque */}
        <Section
          id="tlaquepaque"
          testid="tlaquepaque"
          name={t.tlaquepaque.name}
          desc={t.tlaquepaque.desc}
          highlightsTitle={t.highlights}
          highlights={t.tlaquepaque.highlights}
          photos={images.tlaquepaque}
          extra={
            <div className="flex items-center gap-2 text-sm text-[#5C6C62]">
              <MapPin className="w-4 h-4 text-[#B85042]" />
              <span>San Pedro Tlaquepaque, Jalisco</span>
            </div>
          }
        />

        {/* 4. Chapala */}
        <Section
          id="chapala"
          testid="chapala"
          name={t.chapala.name}
          desc={t.chapala.desc}
          highlightsTitle={t.highlights}
          highlights={t.chapala.highlights}
          photos={images.chapala}
          extra={
            <div className="flex items-center gap-2 text-sm text-[#5C6C62]">
              <MapPin className="w-4 h-4 text-[#B85042]" />
              <span>Chapala / Ajijic, Jalisco</span>
            </div>
          }
        />

        {/* 5. Fan Fest */}
        <Section
          id="fan-fest"
          testid="fanfest"
          name={t.fanfest.name}
          desc={t.fanfest.desc}
          highlightsTitle={t.highlights}
          highlights={t.fanfest.highlights}
          photos={images.fanfest}
          extra={
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-[#0F2922] font-body font-semibold">
                <Calendar className="w-4 h-4 text-[#B85042]" />
                {t.fanfest.dates}
              </div>
              <a
                href="https://guadalajarafwc26.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="fanfest-website-link"
              >
                <Button className="bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t.visitWebsite}
                </Button>
              </a>
            </div>
          }
        />

        {/* 6. Tonala */}
        <Section
          id="tonala"
          testid="tonala"
          name={t.tonala.name}
          desc={t.tonala.desc}
          highlightsTitle={t.highlights}
          highlights={t.tonala.highlights}
          photos={images.tonala}
          extra={
            <div className="flex items-center gap-2 text-sm text-[#5C6C62]">
              <MapPin className="w-4 h-4 text-[#B85042]" />
              <span>Tonala, Jalisco - 30 min de Guadalajara</span>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default AttractionsPage;
